import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  listbusinessoverview,
  listdashboardbalances,
  listItemCategorymaster,
  listItemDashboardData,
  listItemmastersv2,
  listStockSummary,
  listtop5expense,
  listTotalClosingStock,
} from '../../graphql';

/** Stable RTK Query cache key: refetch only when period, OB flag, company, or FY change. */
export const getDashboardBalancesCacheKey = (args) => {
  if (!args) return 'dashboard-balances-skip';
  const { startdate, enddate, openingbalance, companyid, financialyearid } = args;
  return `dashboard-balances:${companyid}:${financialyearid}:${startdate}:${enddate}:${openingbalance}`;
};

export const getItemStockCacheKey = (args) => {
  if (!args) return 'item-stock-skip';
  const { starttimestamp, endtimestamp } = args;
  return `item-stock:${starttimestamp}:${endtimestamp}`;
};

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery,
  tagTypes: ['dashboard', 'bizview', 'Dashboard_itemMaster', 'Dashboard_itemCategoryMaster', 'listStockSummary'],
  endpoints: (builder) => ({
    fetchDashboardData: builder.query({
      query: (variables) => ({
        body: listdashboardbalances,
        variables,
      }),
      serializeQueryArgs: ({ queryArgs }) => getDashboardBalancesCacheKey(queryArgs),
      providesTags: (result, error, arg) => [
        ...(arg
          ? [{ type: 'dashboard', id: getDashboardBalancesCacheKey(arg) }]
          : []),
        'dashboard',
      ],
      keepUnusedDataFor: 600,
      refetchOnMountOrArgChange: false,
    }),
    fetchBizviewData: builder.query({
      query: (variables) => ({
        body: listbusinessoverview,
        variables,
      }),
      providesTags: (result, error, { startdate, enddate, financialyearid, companyid }) => [
        { startdate, enddate, financialyearid, companyid },
        'bizview',
      ],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: false,
    }),
    fetchBizviewTopExpenses: builder.query({
      query: (variables) => ({
        body: listtop5expense,
        variables,
      }),
      providesTags: (result, error, { startdate, enddate, financialyearid, companyid }) => [
        { startdate, enddate, financialyearid, companyid },
        'bizview',
      ],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: false,
    }),
    fetchDashboardItemMaster: builder.query({
      query: (variables) => ({
        body: listItemmastersv2,
        variables,
      }),
      providesTags: (result, error, { limitno }) => [{ limitno }, 'Dashboard_itemMaster'],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: true,
    }),
    fetchDashboardItemCategoryMaster: builder.query({
      query: (variables) => ({
        body: listItemCategorymaster,
        variables,
      }),
      providesTags: (result, error, { maincategory }) => [{ maincategory }, 'Dashboard_itemCategoryMaster'],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: true,
    }),
    fetchDashboardItemWithStock: builder.query({
      query: (variables) => ({
        body: listItemDashboardData,
        variables,
      }),
      serializeQueryArgs: ({ queryArgs }) => getItemStockCacheKey(queryArgs),
      providesTags: (result, error, { starttimestamp, endtimestamp }) => [
        { type: 'Dashboard_itemMaster', id: getItemStockCacheKey({ starttimestamp, endtimestamp }) },
        'Dashboard_itemMaster',
      ],
      keepUnusedDataFor: 600,
      refetchOnMountOrArgChange: false,
    }),

    fetchListStockSummary: builder.query({
      query: (variables) => ({
        body: listStockSummary,
        variables,
      }),
      providesTags: (result, error, { starttimestamp, endtimestamp }) => [{ starttimestamp, endtimestamp }, 'listStockSummary'],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: false,
    }),

    fetchTotalClosingStock: builder.query({
      query: (variables) => ({
        body: listTotalClosingStock,
        variables,
      }),
      providesTags: (result, error, { starttimestamp, endtimestamp, openingbalance }) => [
        { starttimestamp, endtimestamp, openingbalance },
        'Dashboard_closingStock',
        'dashboard',
      ],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: false,
    }),
  }),
});

export const {
  useFetchListStockSummaryQuery,
  useFetchDashboardDataQuery,
  useFetchBizviewDataQuery,
  useFetchBizviewTopExpensesQuery,
  useFetchDashboardItemMasterQuery,
  useFetchDashboardItemCategoryMasterQuery,
  useFetchDashboardItemWithStockQuery,
  useFetchTotalClosingStockQuery,
} = dashboardApi;
